/**
 * DatePicker — Xero-style date picker component.
 *
 * - Display format: "15 Apr 2026" (locale-aware month abbreviation)
 * - Keyboard input: accepts "15 Apr 2026" (English) or "15/04/2026"
 * - Internal value: ISO date string (YYYY-MM-DD)
 * - Calendar popover: month grid with prev/next navigation
 * - Cleared value: `onChange('')` — see `EMPTY_DATE` below
 *
 * UI labels default to English. Pass `labels` prop to override (e.g. from i18n).
 * Pass `locale` prop (e.g. `i18n.language`) to make month/day names locale-aware.
 */
import * as React from 'react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/popover';
import { StyledSelect } from '../styled_select/styled_select';
import { cn } from '../../lib/cn';
import { get_month_names, get_weekday_names } from '../../lib/intl_names';
import {
  build_year_options,
  parse_relative_input,
} from './date_picker_calculations';

/**
 * The value `onChange` receives when the user clears the field (calendar
 * "Clear" button, or emptying the text input and blurring).
 *
 * `''` rather than `null` because that is what every consumer already uses
 * as its empty sentinel: date state is typed `string` (react-hook-form
 * fields default to `''`, `useState<string>('')` in the report filter bars)
 * and rendered as `value={the_date || null}`. Keeping `onChange`'s parameter
 * a plain `string` means a cleared value round-trips through the existing
 * `x || null` / `x || undefined` boundaries — and no call site has to widen
 * its `Dispatch<SetStateAction<string>>` setter.
 */
const EMPTY_DATE = '';

/** English month abbreviations kept for parsing user text input (DD Mon YYYY). */
const MONTH_ABBR_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const DEFAULT_LABELS: DatePickerLabels = {
  placeholder: 'DD Mon YYYY',
  previous_month: 'Previous month',
  next_month: 'Next month',
  open_calendar: 'Open calendar',
  clear: 'Clear',
  today: 'Today',
  select_month: 'Month',
  select_year: 'Year',
};

/**
 * Compact trigger for the calendar-header Month / Year `StyledSelect`s: the
 * system `.nv-input` dropdown look, shrunk to fit the 16rem calendar. `!` is
 * needed because `.nv-input` is unlayered CSS whose padding and font-size
 * otherwise beat Tailwind utilities (the default trigger truncated "2026").
 */
const HEADER_SELECT_TRIGGER =
  'nv-input flex h-8 w-full cursor-pointer items-center justify-between !px-2 !py-0 !text-[13px] font-medium';

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function format_display(iso: string, locale = 'en'): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const month_abbr = get_month_names(locale, 'short')[m - 1];
  return `${String(d)} ${month_abbr} ${String(y)}`;
}

function parse_input(raw: string, locale = 'en'): string | null {
  const trimmed = raw.trim();

  const slash_match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slash_match) {
    const [, dd, mm, yyyy] = slash_match;
    const d = parseInt(dd, 10);
    const m = parseInt(mm, 10);
    const y = parseInt(yyyy, 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${String(y)}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }

  const text_match = /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/.exec(trimmed);
  if (text_match) {
    const [, dd, mon, yyyy] = text_match;
    // Try English abbreviations first for backwards compatibility
    let m = MONTH_ABBR_EN.findIndex(
      (a) => a.toLowerCase() === mon.slice(0, 3).toLowerCase(),
    );
    // If not found, try locale-aware abbreviations
    if (m === -1) {
      const locale_month_abbr = get_month_names(locale, 'short');
      m = locale_month_abbr.findIndex(
        (a) => a.toLowerCase() === mon.slice(0, 3).toLowerCase(),
      );
    }
    if (m !== -1) {
      const d = parseInt(dd, 10);
      const y = parseInt(yyyy, 10);
      if (d >= 1 && d <= 31) {
        return `${String(y)}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
    }
  }

  return null;
}

function today_iso(): string {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function build_calendar(year: number, month: number): (number | null)[][] {
  const first_day = new Date(year, month, 1).getDay();
  const days_in_month = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = Array.from(
    { length: first_day },
    () => null,
  );
  for (let d = 1; d <= days_in_month; d++) {
    cells.push(d);
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  const rows: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
}

interface CalendarProps {
  value: string | null;
  on_select: (iso: string) => void;
  /** Omit (with `on_today`) to render the grid without the Clear/Today footer. */
  on_clear?: () => void;
  on_today?: () => void;
  labels: DatePickerLabels;
  locale: string;
  /** ISO `YYYY-MM-DD`; days before this are disabled. */
  min?: string;
  /** ISO `YYYY-MM-DD`; days after this are disabled. */
  max?: string;
}

/**
 * Range check for a calendar day. ISO `YYYY-MM-DD` strings are
 * lexicographically ordered, so plain string comparison is correct here and
 * avoids constructing Dates (which would drag timezone handling in).
 */
function is_out_of_range(iso: string, min?: string, max?: string): boolean {
  if (min !== undefined && min !== '' && iso < min) return true;
  if (max !== undefined && max !== '' && iso > max) return true;
  return false;
}

function iso_for_day(year: number, month: number, day: number): string {
  return `${String(year)}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function CalendarGrid({
  value,
  on_select,
  on_clear,
  on_today,
  labels,
  locale,
  min,
  max,
}: CalendarProps) {
  const parsed = value ? value.split('-').map(Number) : null;
  const [view_year, set_view_year] = useState(
    parsed ? parsed[0] : new Date().getFullYear(),
  );
  const [view_month, set_view_month] = useState(
    parsed ? parsed[1] - 1 : new Date().getMonth(),
  );

  const month_names_long = useMemo(
    () => get_month_names(locale, 'long'),
    [locale],
  );
  const day_names = useMemo(
    () => get_weekday_names(locale, 'narrow'),
    [locale],
  );
  const full_day_names = useMemo(
    () => get_weekday_names(locale, 'long'),
    [locale],
  );

  const today = today_iso();

  // Roving tabindex: which day ISO string currently has tabIndex=0
  const [focused_iso, set_focused_iso] = useState<string>(() => value ?? today);
  const grid_ref = useRef<HTMLDivElement>(null);
  // Set to true when a keydown navigation should programmatically focus the new day
  const needs_focus = useRef(false);

  // After state updates from keyboard nav, move DOM focus to the newly focused day button
  useEffect(() => {
    if (needs_focus.current && grid_ref.current) {
      needs_focus.current = false;
      const btn = grid_ref.current.querySelector<HTMLButtonElement>(
        'button[tabindex="0"]',
      );
      btn?.focus();
    }
  }, [focused_iso]);

  function shift_to_month(new_year: number, new_month: number): string {
    const [, , d] = focused_iso.split('-').map(Number);
    const days_in_month = new Date(new_year, new_month + 1, 0).getDate();
    return iso_for_day(new_year, new_month, Math.min(d, days_in_month));
  }

  function prev_month() {
    const new_month = view_month === 0 ? 11 : view_month - 1;
    const new_year = view_month === 0 ? view_year - 1 : view_year;
    set_view_month(new_month);
    set_view_year(new_year);
    set_focused_iso(shift_to_month(new_year, new_month));
  }

  function next_month() {
    const new_month = view_month === 11 ? 0 : view_month + 1;
    const new_year = view_month === 11 ? view_year + 1 : view_year;
    set_view_month(new_month);
    set_view_year(new_year);
    set_focused_iso(shift_to_month(new_year, new_month));
  }

  function jump_to(new_year: number, new_month: number) {
    set_view_month(new_month);
    set_view_year(new_year);
    set_focused_iso(shift_to_month(new_year, new_month));
  }

  const month_options = useMemo(
    () => month_names_long.map((label, i) => ({ value: String(i), label })),
    [month_names_long],
  );
  const year_options = useMemo(
    () =>
      build_year_options(view_year, min, max).map((y) => ({
        value: String(y),
        label: String(y),
      })),
    [view_year, min, max],
  );

  const rows = build_calendar(view_year, view_month);

  function make_iso(day: number): string {
    return iso_for_day(view_year, view_month, day);
  }

  function handle_day_key_down(
    e: React.KeyboardEvent<HTMLButtonElement>,
    current_iso: string,
  ) {
    const [y, m, d] = current_iso.split('-').map(Number);
    let target: Date | null = null;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      target = new Date(y, m - 1, d - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      target = new Date(y, m - 1, d + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      target = new Date(y, m - 1, d - 7);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      target = new Date(y, m - 1, d + 7);
    }

    if (!target) return;

    const new_year = target.getFullYear();
    const new_month = target.getMonth();
    const new_iso = iso_for_day(new_year, new_month, target.getDate());

    if (new_month !== view_month || new_year !== view_year) {
      set_view_year(new_year);
      set_view_month(new_month);
    }

    needs_focus.current = true;
    set_focused_iso(new_iso);
  }

  return (
    <div className="w-64 select-none">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={prev_month}
          className="rounded-lg p-1.5 hover:bg-[var(--accent)]/10 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          aria-label={labels.previous_month}
        >
          <ChevronLeftIcon className="h-4 w-4 text-[var(--ink-muted)]" />
        </button>
        <div className="mx-1 flex min-w-0 flex-1 items-center gap-1">
          <div className="min-w-0 flex-1">
            <StyledSelect
              value={String(view_month)}
              onChange={(m) => {
                jump_to(view_year, Number(m));
              }}
              options={month_options}
              ariaLabel={labels.select_month}
              ariaInvalid={false}
              triggerClassName={HEADER_SELECT_TRIGGER}
            />
          </div>
          <div className="w-[4.75rem] shrink-0">
            <StyledSelect
              value={String(view_year)}
              onChange={(y) => {
                jump_to(Number(y), view_month);
              }}
              options={year_options}
              ariaLabel={labels.select_year}
              ariaInvalid={false}
              triggerClassName={HEADER_SELECT_TRIGGER}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={next_month}
          className="rounded-lg p-1.5 hover:bg-[var(--accent)]/10 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          aria-label={labels.next_month}
        >
          <ChevronRightIcon className="h-4 w-4 text-[var(--ink-muted)]" />
        </button>
      </div>

      <div
        ref={grid_ref}
        role="grid"
        aria-label={`${month_names_long[view_month] ?? ''} ${String(view_year)}`}
      >
        <div role="row" className="mb-1 grid grid-cols-7">
          {day_names.map((d, i) => (
            <div
              key={i}
              role="columnheader"
              aria-label={full_day_names[i]}
              className="flex items-center justify-center text-xs font-medium uppercase tracking-wide text-[var(--ink-muted)]"
            >
              {d}
            </div>
          ))}
        </div>

        {rows.map((row, ri) => (
          <div key={ri} role="row" className="grid grid-cols-7">
            {row.map((day, ci) => {
              if (!day) {
                return (
                  <div
                    key={`empty-${String(ri)}-${String(ci)}`}
                    role="gridcell"
                    className="h-9 w-full"
                  />
                );
              }
              const iso = make_iso(day);
              const is_selected = iso === value;
              const is_today = iso === today;
              // min/max are documented as disabling out-of-range days. They
              // were previously declared on DatePickerProps but never read,
              // so a caller's constraint was silently ignored.
              const is_disabled = is_out_of_range(iso, min, max);
              return (
                <div key={iso} role="gridcell" aria-selected={is_selected}>
                  <button
                    type="button"
                    tabIndex={iso === focused_iso ? 0 : -1}
                    disabled={is_disabled}
                    aria-disabled={is_disabled}
                    onClick={() => {
                      if (is_disabled) return;
                      on_select(iso);
                    }}
                    onFocus={() => {
                      set_focused_iso(iso);
                    }}
                    onKeyDown={(e) => {
                      handle_day_key_down(e, iso);
                    }}
                    className={cn(
                      'flex h-9 w-full items-center justify-center rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
                      is_disabled
                        ? 'cursor-not-allowed text-[var(--ink-faint)] opacity-40'
                        : is_selected
                          ? 'bg-[var(--accent)] font-semibold text-white hover:bg-[var(--accent)]/90'
                          : is_today
                            ? 'font-semibold text-[var(--accent)] ring-1 ring-[var(--accent)]/50 hover:bg-[var(--accent)]/10'
                            : 'text-[var(--ink)] hover:bg-[var(--accent)]/10 hover:text-[var(--accent)]',
                    )}
                    aria-label={format_display(iso)}
                  >
                    {day}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {(on_clear ?? on_today) && (
        <div className="mt-2 flex items-center justify-between border-t border-[var(--line)] pt-2">
          {on_clear ? (
            <button
              type="button"
              onClick={on_clear}
              className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {labels.clear}
            </button>
          ) : (
            <span />
          )}
          {on_today && (
            <button
              type="button"
              onClick={on_today}
              className="text-sm font-medium text-[var(--accent)] hover:text-[var(--accent)]/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {labels.today}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Guard for any Radix `PopoverContent` that hosts the calendar. Pressing
 * Escape while a Month / Year `StyledSelect` list is open must close only that
 * list: Radix listens for Escape on `document`, so without this it would also
 * dismiss the whole calendar. (Clicks on the portaled options need no guard:
 * `StyledSelect` portals with React's `createPortal`, and Radix's
 * DismissableLayer tracks "inside" through the React tree, so those clicks
 * already count as inside the popover.)
 */
export const calendarPopoverGuards = {
  onEscapeKeyDown: (e: KeyboardEvent): void => {
    if (
      e.target instanceof Element &&
      e.target.getAttribute('aria-haspopup') === 'listbox' &&
      e.target.getAttribute('aria-expanded') === 'true'
    ) {
      e.preventDefault();
    }
  },
};

export interface DateCalendarProps {
  /** Selected ISO `YYYY-MM-DD` date, or `null` for none. */
  value: string | null;
  onSelect: (iso: string) => void;
  /** Renders a "Clear" footer action when provided. */
  onClear?: () => void;
  /** Renders a "Today" footer action when provided. */
  onToday?: () => void;
  labels?: Partial<DatePickerLabels>;
  /** BCP 47 locale tag for month / weekday names. Defaults to `'en'`. */
  locale?: string;
  min?: string;
  max?: string;
}

/**
 * The DatePicker's calendar grid (Month / Year dropdowns, day grid) without
 * the text input or popover — for composite controls that embed a calendar
 * in their own popover (e.g. a preset menu with a "Custom date" view). Spread
 * {@link calendarPopoverGuards} onto that popover's content.
 */
export function DateCalendar({
  value,
  onSelect,
  onClear,
  onToday,
  labels: labels_override,
  locale = 'en',
  min,
  max,
}: DateCalendarProps) {
  return (
    <CalendarGrid
      value={value}
      on_select={onSelect}
      on_clear={onClear}
      on_today={onToday}
      labels={{ ...DEFAULT_LABELS, ...labels_override }}
      locale={locale}
      min={min}
      max={max}
    />
  );
}

export interface DatePickerLabels {
  placeholder: string;
  previous_month: string;
  next_month: string;
  open_calendar: string;
  clear: string;
  today: string;
  /** Accessible name for the Month dropdown in the calendar header. */
  select_month?: string;
  /** Accessible name for the Year dropdown in the calendar header. */
  select_year?: string;
}

export interface DatePickerProps {
  value: string | null;
  /**
   * Called with an ISO `YYYY-MM-DD` string when a date is picked or typed,
   * and with `''` when the user clears the field (calendar "Clear", or
   * emptying the text input and blurring). Consumers that treat the date as
   * optional should map `''` to `null`/`undefined` at their API boundary;
   * consumers with a required date should let their own validation surface
   * the empty value as an error.
   */
  onChange: (iso: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /**
   * Marks the underlying text input as required for assistive tech. The
   * component performs no validation of its own — the consuming form owns
   * that; this only mirrors the requirement to the accessibility tree.
   */
  'aria-required'?: boolean;
  /**
   * Forwarded to the underlying text input so a save-in-flight state on the
   * consuming form is announced by assistive tech on the actual control
   * rather than on a wrapper node.
   */
  'aria-busy'?: boolean;
  /** Forwarded to the root wrapper div for test selection (e.g. data-testid). */
  'data-testid'?: string;
  /** Override individual UI strings (defaults are English). */
  labels?: Partial<DatePickerLabels>;
  /**
   * BCP 47 locale tag (e.g. `'si'`, `'ta'`, `'id'`, `'en'`).
   * Controls month and weekday names in the calendar popover and the display
   * format of the text input. Defaults to `'en'`.
   * Typically set to `i18n.language` from `useTranslation`.
   */
  locale?: string;
  /** ISO date string (YYYY-MM-DD). Days before this date are disabled. */
  min?: string;
  /** ISO date string (YYYY-MM-DD). Days after this date are disabled. */
  max?: string;
  /**
   * When provided, the text input also accepts a relative day offset in the
   * form `+7`, `+30`, `+365` — resolved as that many days after
   * `relativeBase` (an ISO `YYYY-MM-DD` string, e.g. a form's Issue Date).
   * On Enter/blur the input is replaced with the calculated calendar date,
   * same as typing an absolute date. Omit (or pass `null`/`''`) to disable —
   * every other consumer is unaffected by default.
   */
  relativeBase?: string | null;
  /**
   * Called (in addition to `onChange`) only when the text just resolved via
   * a relative offset (see `relativeBase`) — never for a typed absolute
   * date, calendar click, or Today/Clear. `days` is the parsed offset;
   * `iso` is the resulting date. Lets a consuming form remember "this due
   * date tracks Issue Date + N days" so it can recompute automatically if
   * Issue Date changes later — the DatePicker itself has no such memory,
   * since once resolved the field displays an ordinary absolute date.
   */
  onRelativeInput?: (days: number, iso: string) => void;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  className,
  id,
  'aria-label': aria_label,
  'aria-labelledby': aria_labelledby,
  'aria-describedby': aria_describedby,
  'aria-invalid': aria_invalid,
  'aria-required': aria_required,
  'aria-busy': aria_busy,
  'data-testid': data_testid,
  labels: labels_override,
  locale = 'en',
  min,
  max,
  relativeBase,
  onRelativeInput,
}: DatePickerProps) {
  const labels: DatePickerLabels = { ...DEFAULT_LABELS, ...labels_override };
  const resolvedPlaceholder = placeholder ?? labels.placeholder;
  const [open, set_open] = useState(false);
  const [input_text, set_input_text] = useState(
    value ? format_display(value, locale) : '',
  );
  const [editing, set_editing] = useState(false);
  const input_ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) {
      set_input_text(value ? format_display(value, locale) : '');
    }
  }, [value, editing, locale]);

  function handle_focus() {
    set_editing(true);
  }

  function handle_blur() {
    set_editing(false);
    // An emptied (or whitespace-only) field is an intentional clear, not
    // invalid input: propagate it so the consuming form actually drops the
    // date. Previously this branch re-rendered the OLD value, so the field
    // looked empty while the form still held — and submitted — the old date.
    if (!input_text.trim()) {
      set_input_text('');
      if (value) onChange(EMPTY_DATE);
      return;
    }
    const relative = parse_relative_input(input_text, relativeBase);
    if (relative) {
      onChange(relative.iso);
      set_input_text(format_display(relative.iso, locale));
      onRelativeInput?.(relative.days, relative.iso);
      return;
    }
    const iso = parse_input(input_text, locale);
    if (iso) {
      onChange(iso);
      set_input_text(format_display(iso, locale));
    } else {
      // Unparseable text is a typo, not a clear — restore the last good value.
      set_input_text(value ? format_display(value, locale) : '');
    }
  }

  function handle_key_down(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      const relative = parse_relative_input(input_text, relativeBase);
      if (relative) {
        onChange(relative.iso);
        set_input_text(format_display(relative.iso, locale));
        onRelativeInput?.(relative.days, relative.iso);
        set_open(false);
        input_ref.current?.blur();
        return;
      }
      const iso = parse_input(input_text, locale);
      if (iso) {
        onChange(iso);
        set_input_text(format_display(iso, locale));
        set_open(false);
        input_ref.current?.blur();
      }
    }
    if (e.key === 'Escape') {
      set_input_text(value ? format_display(value, locale) : '');
      set_open(false);
      input_ref.current?.blur();
    }
  }

  function handle_calendar_select(iso: string) {
    onChange(iso);
    set_input_text(format_display(iso, locale));
    set_open(false);
  }

  function handle_calendar_clear() {
    set_input_text('');
    set_editing(false);
    set_open(false);
    // Only emit when there is something to clear, so clicking Clear on an
    // already-empty field does not dirty the consuming form.
    if (value) onChange(EMPTY_DATE);
  }

  function handle_calendar_today() {
    const iso = today_iso();
    onChange(iso);
    set_input_text(format_display(iso, locale));
    set_open(false);
  }

  return (
    <Popover open={open} onOpenChange={set_open}>
      <PopoverTrigger asChild>
        <div
          data-testid={data_testid}
          className={cn(
            'nv-input flex h-11 w-full items-center sm:h-9',
            error && 'is-invalid border-error',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          <input
            ref={input_ref}
            id={id}
            type="text"
            value={input_text}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            className="min-w-0 flex-1 bg-transparent focus:outline-none placeholder:text-[var(--ink-faint)] disabled:cursor-not-allowed"
            onFocus={handle_focus}
            onBlur={handle_blur}
            onChange={(e) => {
              set_input_text(e.target.value);
            }}
            onKeyDown={handle_key_down}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            aria-label={aria_label ?? labels.placeholder}
            aria-labelledby={aria_labelledby}
            aria-describedby={aria_describedby}
            aria-invalid={aria_invalid}
            aria-required={aria_required}
            aria-busy={aria_busy}
            autoComplete="off"
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                set_open((o) => !o);
                input_ref.current?.focus();
              }
            }}
            className="ms-1 shrink-0 rounded p-0.5 text-[var(--ink-faint)] hover:text-[var(--ink-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] disabled:cursor-not-allowed"
            aria-label={labels.open_calendar}
            tabIndex={-1}
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto border-[var(--line)] p-3"
        {...calendarPopoverGuards}
      >
        <CalendarGrid
          value={value}
          on_select={handle_calendar_select}
          on_clear={handle_calendar_clear}
          on_today={handle_calendar_today}
          labels={labels}
          locale={locale}
          min={min}
          max={max}
        />
      </PopoverContent>
    </Popover>
  );
}
